import React, { useState, useCallback } from 'react';
import { Control, Controller } from 'react-hook-form';
import { Box, TextField } from '@mui/material';
import {
  ApiManagementFormState,
  ExchangeCredentialState
} from './api-management-form';
import { FieldWrapper } from '../../../foundation/layout/field-wrapper';
import { SubmitButton } from '../../../foundation/form/buttons/submit-button';
import { Section } from '../../../foundation/layout/section';
import { FormContainer } from '../../../foundation/layout/form-container';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { AppIconButton } from '@client/foundation/design-system/components';

type ApiKeyFormProps = {
  title: string;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onDelete: (index: number) => void;
  control: Control<ApiManagementFormState, any>;
  index: number;
  apiKeyName: keyof ExchangeCredentialState;
  apiSecretName: keyof ExchangeCredentialState;
  hasApiKeyValue: boolean;
  hasApiSecretValue: boolean;
};

export const ApiKeyForm: React.FC<ApiKeyFormProps> = ({
  title,
  onSubmit,
  onDelete,
  control,
  index,
  apiKeyName,
  apiSecretName,
  hasApiKeyValue,
  hasApiSecretValue
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const toggleEditing = useCallback(() => {
    setIsEditing((prev) => !prev);
  }, []);

  const MASK = '***********';

  const renderTextField = (
    field: any,
    label: string,
    isConfigured: boolean
  ) => (
    <TextField
      {...field}
      label={label}
      value={isConfigured && !isEditing ? MASK : field.value || ''}
      onChange={field.onChange}
      disabled={!isEditing}
    />
  );

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Section title={title}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 0.75
          }}
        >
          <AppIconButton
            title={isEditing ? 'Cancel Editing' : 'Edit'}
            tone="primary"
            onClick={toggleEditing}
          >
            <EditIcon />
          </AppIconButton>
          <AppIconButton title="Delete" tone="danger" onClick={() => onDelete(index)}>
            <DeleteIcon />
          </AppIconButton>
        </Box>
        <FormContainer layout="column">
          <FieldWrapper>
            <Controller
              name={`exchanges.${index}.apiKeys.${apiKeyName}`}
              control={control}
              render={({ field }) =>
                renderTextField(field, 'API Key', hasApiKeyValue)
              }
            />
          </FieldWrapper>
          <FieldWrapper>
            <Controller
              name={`exchanges.${index}.apiKeys.${apiSecretName}`}
              control={control}
              render={({ field }) =>
                renderTextField(field, 'API Secret', hasApiSecretValue)
              }
            />
            {isEditing && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <SubmitButton />
              </Box>
            )}
          </FieldWrapper>
        </FormContainer>
      </Section>
    </Box>
  );
};
